import type { Challenge } from './types';
import type { Profile } from '../lib/state';

// These must match SLOT_OF in the backend's kindling-crew.service.ts. "a voice"
// matched nothing, so that slot could never fill; style finds come back as "a look".
export const SLOTS = ['someone', 'somewhere', 'a want', 'a look', 'a line'] as const;
export type Slot = (typeof SLOTS)[number];

/**
 * A recipe: slots that exercise outputs are dealt into, some required, and
 * one thing made from the row (owner, 2026-09-22: "this basic structure, or
 * merging activity outputs into things, may be re-usable… turn an art style +
 * a want into a character"). The film is the first recipe; the table
 * (components/FilmTable.tsx) and the collection (lib/pieces.ts) are written
 * against SLOTS today and this is where a second recipe would be declared:
 * `{ id: 'character', slots: ['a look', 'a want', 'someone'], required: ['a look', 'a want'], makes: 'a character sheet' }`.
 */
export interface Recipe { id: string; slots: readonly Slot[]; required: readonly Slot[]; makes: string; verb: string }

/** What an exercise `gives` (14-exercises-v2.md), as the film slot it can fill. `scrap` fills nothing. */
const SLOT_OF_GIVES: Record<string, Slot> = { person: 'someone', place: 'somewhere', story: 'a want', picture: 'a look', film: 'a line' };

/**
 * A card pencilled into a slot of the first film, until the crew's find (a kept
 * one) takes it. `pictureUrl` is the crew's drawing of it, when that is what
 * landed (the Day 1 hero).
 */
export interface Pencilled { challengeId: string; title: string; headline: string; cardUrl?: string; pictureUrl?: string; postId?: string; at: number }

/**
 * The slots this exercise can fill, in the order the exercise lists them. A
 * private page feeds nothing, and neither does a witness exercise: the only
 * person in it is a real friend, and a friend is never seated as Starring
 * (14 §11.1; activities audit §3.4, "Starring: Maya").
 */
export function slotsFor(c: Challenge): Slot[] {
  if (c.input.private || c.purpose === 'open' || c.witness) return [];
  return [...new Set((c.gives || []).map((g) => SLOT_OF_GIVES[g]).filter(Boolean))];
}

/**
 * How squarely an exercise fills a slot: 0 when it routes a part there on
 * purpose (`input.slots`, a closing field included), 1 when the slot is the
 * first it gives (where its unnamed parts land), 2 when it only lists it. Home
 * used to sort the gap's tiles by minutes alone, so the `someone` gap showed
 * two-minute exercises that give a person in passing and never Interview the
 * Character (activities audit §3.1). Null when it cannot fill the slot at all.
 */
export function feedRank(c: Challenge, slot: Slot): 0 | 1 | 2 | null {
  const mine = slotsFor(c);
  if (!mine.includes(slot)) return null;
  if (Object.values(c.input.slots || {}).includes(slot)) return 0;
  return mine[0] === slot ? 1 : 2;
}

/**
 * Where a card just made goes: the first of its slots nothing is in yet, else
 * the first one only pencilled (a newer card replaces an older one). A slot the
 * crew filled with something the person kept is never taken.
 */
export function slotForCard(c: Challenge, p: Profile, kept: Set<string>): Slot | null {
  const mine = slotsFor(c).filter((s) => !kept.has(s));
  return mine.find((s) => !p.pencilled?.[s]) ?? mine[0] ?? null;
}

/** What the film's title card calls each slot: its credit, and what an empty one says (K19 H-06b). */
export const CREDIT: Record<Slot, { label: string; empty: string; line: string }> = {
  someone: { label: 'Starring', empty: 'someone · not found yet', line: 'Your film follows them.' },
  somewhere: { label: 'Set in', empty: 'somewhere · not found yet', line: 'Your film is happening here.' },
  'a want': { label: 'Who wants', empty: 'something · not found yet', line: 'Someone in your film wants this.' },
  'a look': { label: 'Looks like', empty: 'a look · not found yet', line: 'Your film looks like this.' },
  'a line': { label: 'First line', empty: 'a line · not written yet', line: 'Your film starts with this line.' },
};

/**
 * Where a picture goes, when the thing landing is the crew's drawing rather
 * than the card: a drawing of a bad idea at the airport is a place before it is
 * a want. Same rules as slotForCard otherwise.
 */
export function slotForPicture(c: Challenge, p: Profile, kept: Set<string>): Slot | null {
  const mine = slotsFor(c).filter((s) => !kept.has(s));
  const visual: Slot[] = ['somewhere', 'a look', 'someone'];
  const ordered = [...mine.filter((s) => visual.includes(s)), ...mine.filter((s) => !visual.includes(s))];
  return ordered.find((s) => !p.pencilled?.[s]) ?? ordered[0] ?? null;
}

/**
 * What the exercise grew, split for two screens (owner, 2026-09-21): the first
 * sentence goes straight under the card, brief; the rest waits for the film
 * screen, which has room to say why it mattered.
 */
export function whatItGrew(c: Challenge): { brief: string; more: string } {
  const all = (c.motivation || c.why || '').trim();
  const m = all.match(/^.+?[.!?](?=\s|$)/);
  const brief = m ? m[0] : all;
  return { brief, more: all.slice(brief.length).trim() };
}

/**
 * Why a film, in terms of what the person said they would like to be (the
 * intro's second question). Making a film is part of the goal whether or not
 * they knew it: every kind of making ends up in one.
 */
const FILM_FOR: Record<string, string> = {
  writer: 'A film is a story people can see. Everything a writer does is in it.',
  artist: 'A film is pictures in order. Every frame of this one comes from you.',
  filmmaker: 'This is the one you came for. It starts as five small pieces.',
  influencer: 'A film is how a story travels. Yours is starting here.',
  business: 'A film is an idea people can feel. Selling any idea starts with one.',
  dreamer: 'A film is a dream you can show somebody else.',
  creator: 'A film pulls every kind of making into one thing.',
};

/**
 * What a roll needs (owner, 2026-09-21: three, not five). A look and a line
 * make the clip better and are never required, or the row would never fill:
 * only one exercise in the deck tends to give a look.
 */
export const REQUIRED: Slot[] = ['someone', 'somewhere', 'a want'];
export const OPTIONAL: Slot[] = ['a look', 'a line'];

/** The slot the next exercise should aim at: the first required one empty, then the first optional one, then none. */
export function nextGap(filled: Set<string>): Slot | null {
  return [...REQUIRED, ...OPTIONAL].find((s) => !filled.has(s)) ?? null;
}

export const FILM: Recipe = { id: 'film', slots: SLOTS, required: ['someone', 'somewhere', 'a want'], makes: 'a ten-second clip', verb: 'Roll it' };

export function filmReady(filled: Set<string>): boolean {
  return REQUIRED.every((s) => filled.has(s));
}

/** A piece's text as a name: the first clause, short enough for a headline ("Nightingale, a traffic cone who…" → "Nightingale"). */
export function shortName(text: string | undefined, max = 28): string | undefined {
  if (!text) return undefined;
  const first = text.split(/[,.;:(—–]/)[0].trim();
  const t = first || text.trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/**
 * The line over the tiles: what the film has, and what the gap asks for, in
 * the film's own nouns (K34 N-03 / N-05). `has` is the text of each filled slot.
 */
export function gapLine(gap: Slot | null, has: Partial<Record<Slot, string>>): { kicker: string; line: string } {
  const who = shortName(has.someone);
  const where = shortName(has.somewhere);
  switch (gap) {
    case 'someone': return { kicker: 'So, today', line: 'Every film starts with someone. These find them.' };
    case 'somewhere': return { kicker: 'So, today', line: who ? `${who} needs somewhere to be. These find it.` : 'Your film needs somewhere to happen. These find it.' };
    case 'a want': return { kicker: 'So, today', line: who ? `${who} has ${where ? 'a place' : 'a name'}. What do they want${where ? ' there' : ''}? These find out.` : 'Someone in your film wants something. These find it.' };
    case 'a look': return { kicker: 'Ready to roll', line: 'Three pieces in. A look would make it yours; or roll it as it is.' };
    case 'a line': return { kicker: 'Ready to roll', line: 'A first line would give it a voice; or roll it as it is.' };
    default: return { kicker: 'Ready to roll', line: 'The row is full. Roll it, or keep adding pieces.' };
  }
}

export function whyAFilm(dreams: string[] | undefined): { said?: string; line: string } {
  const id = (dreams || []).find((d) => FILM_FOR[d]);
  const generic = 'A film needs a bit of everything: a story, a place, a look, a voice. So every exercise here is building one, whether you knew it or not.';
  return id ? { said: id, line: FILM_FOR[id] } : { line: generic };
}
